import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { FileManagerService } from '../../core/services/files/file-manager.service';
import { ModalConfirmationComponent } from '../../utils/modal-confirmation/modal-confirmation.component';
import { FileData, MassiveLoadResult } from './../../core/model/massive-load-result';
import { CargaMasivaService } from './../../core/services/carga-masiva.service';
import { UtilitiesService } from '../../core/services/Utility/utilities.service';

@Component({
  selector: 'app-carga-masiva',
  templateUrl: './carga-masiva.component.html',
  styleUrls: ['./carga-masiva.component.css']
})
export class CargaMasivaComponent implements OnInit {

  @Input() templateId: number;
  @ViewChild('fileInputMasiva') fileInput;
  onClose = new Subject<MassiveLoadResult>();
  onFileUploaded = new Subject<FileData>();
  fileName = '';
  resultadoValidacionFichero: MassiveLoadResult;
  thinking = false;
  loading = false;

  customMessages = {
    emptyMessage: 'No hay datos que mostrar',
    totalMessage: 'Total',
    selectedMessage: 'Selección'
  };

  customClasses = {
    sortAscending: 'fas fa-sort-up',
    sortDescending: 'fas fa-sort-down',
    pagerLeftArrow: 'fas fa-chevron-left',
    pagerRightArrow: 'fas fa-chevron-right',
    pagerPrevious: 'fas fa-step-backward',
    pagerNext: 'fas fa-step-forward'
  };

  autoLoad: boolean;
  showConfirmationModal = true;

  constructor(
    private modalRef: BsModalRef,
    private modalService: BsModalService,
    private cargaMasivaService: CargaMasivaService,
    private fileManagerService: FileManagerService,
    private toastService: ToastrService,
    private utilsService: UtilitiesService
  ) { }

  ngOnInit() {
  }

  changeFileName() {
    this.fileName = this.fileInput.nativeElement.files[0].name;
  }

  downloadTemplate() {
    this.thinking = true;
    this.cargaMasivaService.getEmptyTemplateFile(this.templateId)
      .subscribe(result => {
        this.utilsService.downloadGenericFile('PantillaCargaMasiva.xlsx', result);
        this.thinking = false;
      }, error => this.thinking = false);
  }

  loadFile() {
    if (this.canValidate()) {
      this.thinking = true;
      const file: File = this.fileInput.nativeElement.files[0];
      this.cargaMasivaService.validateFile(this.templateId, file)
        .subscribe((result: MassiveLoadResult) => {
          this.thinking = false;
          this.resultadoValidacionFichero = result;
          if (this.autoLoad) {
            const resultType = this.resultType(result);
            if (resultType === 'OK') {
              setTimeout(() => {
                this.onClose.next(this.resultadoValidacionFichero);
                const fileData: FileData = this.createFileData(file);
                this.onFileUploaded.next(fileData);
                this.closeModal();
              }, 500);

            } else if (resultType === 'ERROR_FORMATO') {
              this.loading = true;
              // DDA 21/01/2019 => Cuando el fichero no se reconoce por su formato para la carga masiva
              // debemos pedir confirmacion al cliente para que diga si está de acuerdo por si se ha
              // equivocado de fichero.
              this.saveFile('No se ha reconocido el fichero como un modelo estándar, ¿Desea continuar guardando el fichero para su tratamiento?');
            }
          }
        },
          error => {
            this.thinking = false;
            this.toastService.clear();
            this.toastService.error('Ha ocurrido un error al validar el fichero de carga masiva. Por favor, inténtelo de nuevo más tarde');
          });
    }
  }

  private createFileData(file: File, guid?: string): FileData {
    const fileData: FileData = new FileData();
    fileData.filename = file.name;
    fileData.extension = this.getExtension(file.name);
    fileData.size = this.calculateSizeFile(file.size);
    fileData.file = file;
    fileData.guid = guid;

    return fileData;
  }

  private getExtension(filename: string): string {
    const splitPoint = filename.split('.');
    return splitPoint[splitPoint.length - 1];
  }

  private calculateSizeFile(size: number): string {
    const sizeInKb = Math.ceil(size / 1000);
    if (sizeInKb < 1000) {
      return sizeInKb + ' Kb';
    }

    return Math.ceil(sizeInKb / 1000) + ' Mb';
  }

  saveFile(linea1: string = 'Se procederá a la carga de los registros. ¿Desea continuar?', linea2: string = '') {
    const file: File = this.fileInput.nativeElement.files[0];
    if (this.showConfirmationModal) {
      const resultType = this.resultType(this.resultadoValidacionFichero);
      const modal = this.modalService.show(ModalConfirmationComponent);
      (<ModalConfirmationComponent>modal.content).showConfirmationModal(
        'Carga de fichero',
        linea1,
        linea2,
        'Sí',
        'No',
        true,
        'fa-exclamation-triangle fa-3x',
        'bg-danger'
      );
      (<ModalConfirmationComponent>modal.content).onClose.subscribe(result => {
        setTimeout(() => {
          if (result === true && (resultType === 'OK' || resultType == 'ERROR_FORMATO')) {
            this.onClose.next(this.resultadoValidacionFichero);
            const fileData: FileData = this.createFileData(file);
            this.onFileUploaded.next(fileData);
          }
          this.closeModal();
        }, 500);
      });
    } else {
      this.onClose.next(this.resultadoValidacionFichero);
      const fileData: FileData = this.createFileData(file);
      this.onFileUploaded.next(fileData);
      this.closeModal();
    }
  }

  canValidate(): boolean {
    return this.fileName && this.fileName.trim() !== '' && this.resultType(this.resultadoValidacionFichero) !== 'ERROR_FORMATO'
      && this.resultType(this.resultadoValidacionFichero) !== 'OK';
  }

  resultType(result: MassiveLoadResult): string {
    if (!result) {
      return undefined;
    }
    if (result.isInventario) {
      return 'INVENTARIO';
    }
    if (result.resultado === true) {
      if (!result.erroresValidacion || result.erroresValidacion.length === 0) {
        return 'OK';
      } else {
        return 'WARNING';
      }
    } else {
      if (!result.erroresValidacion && (!result.registrosExcel || result.registrosExcel.length === 0)) {
        return 'ERROR_FORMATO';
      }
      return 'ERROR_REGISTROS';
    }
  }

  closeModal() {
    this.modalRef.hide();
  }

  getResult(): MassiveLoadResult {
    return this.resultadoValidacionFichero;
  }
}
